import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Crown, Trophy, Star, CheckCircle, Calendar, Medal, Sparkles, Target
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BestPerformerCard = ({ businessId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBestPerformer();
  }, [businessId]);

  const fetchBestPerformer = async () => {
    try {
      setLoading(true);
      let url = `${BACKEND_URL}/api/best-performer`;
      if (businessId) {
        url += `?business_id=${businessId}`;
      }
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching best performer:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-amber-50 to-yellow-100 dark:from-amber-900/20 dark:to-yellow-900/20 border-amber-200 dark:border-amber-800 rounded-2xl animate-pulse">
        <CardContent className="p-6">
          <div className="h-48 bg-amber-200/50 rounded-xl"></div>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.leaderboard || data.leaderboard.length === 0) {
    return (
      <Card 
        data-testid="best-performer-card-empty"
        className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl"
      >
        <CardContent className="p-6 text-center">
          <Trophy className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400">Team Leaderboard</h3>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
            No performance data yet this month
          </p>
        </CardContent>
      </Card>
    );
  }

  const { best_performer, leaderboard, current_user_rank, weights, month_name } = data;

  const getRankBadge = (rank) => {
    if (rank === 1) return <Crown className="w-4 h-4 text-amber-500" />;
    if (rank === 2) return <Medal className="w-4 h-4 text-slate-400" />;
    if (rank === 3) return <Medal className="w-4 h-4 text-orange-400" />;
    return <span className="text-xs font-bold text-slate-500">#{rank}</span>;
  };

  const getRankBgColor = (rank) => {
    if (rank === 1) return "bg-gradient-to-br from-amber-400 to-amber-600";
    if (rank === 2) return "bg-gradient-to-br from-slate-300 to-slate-400";
    if (rank === 3) return "bg-gradient-to-br from-orange-300 to-orange-400";
    return "bg-slate-200 dark:bg-slate-700";
  };

  return (
    <Card 
      data-testid="best-performer-card"
      className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-amber-900/30 dark:via-yellow-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-700 rounded-2xl shadow-lg shadow-amber-200/30 overflow-hidden"
    >
      {/* Decorative top bar */}
      <div className="h-2 bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400"></div>
      
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-800 dark:text-white">Team Leaderboard</span>
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">{month_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px]">
              <CheckCircle className="w-3 h-3 mr-1" />
              Tasks {weights?.tasks || 50}%
            </Badge>
            <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px]">
              <Calendar className="w-3 h-3 mr-1" />
              Attend. {weights?.attendance || 50}%
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-2">
        {/* Current User Rank - Show first if not in top 3 */}
        {current_user_rank && current_user_rank.rank > 3 && (
          <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-xl p-3 mb-4 border border-indigo-200 dark:border-indigo-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-500" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Your Rank</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">#{current_user_rank.rank}</span>
                <span className="text-sm text-slate-500">of {current_user_rank.total_participants}</span>
                <span className="text-sm font-semibold text-indigo-600">({current_user_rank.weighted_score} pts)</span>
              </div>
            </div>
          </div>
        )}

        {/* Full Leaderboard - All users ranked */}
        <div className="space-y-2">
          {leaderboard.map((member, index) => {
            const rank = index + 1;
            const isCurrentUser = current_user_rank && member.user_id === current_user_rank.user_id;
            const isBestPerformer = rank === 1;
            
            return (
              <div
                key={member.user_id}
                data-testid={`leaderboard-row-${rank}`}
                className={`p-3 rounded-xl border transition-all ${
                  isBestPerformer 
                    ? "bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-900/40 dark:to-yellow-900/40 border-amber-300 dark:border-amber-700 ring-2 ring-amber-400/30" 
                    : isCurrentUser
                    ? "bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700"
                    : "bg-white/80 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700"
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Rank Badge */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${getRankBgColor(rank)}`}>
                    {getRankBadge(rank)}
                  </div>

                  {/* Profile */}
                  <div className="relative flex-shrink-0">
                    {member.picture ? (
                      <img 
                        src={member.picture} 
                        alt={member.name}
                        className={`w-10 h-10 rounded-full object-cover ${isBestPerformer ? "ring-2 ring-amber-400" : ""}`}
                      />
                    ) : (
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white font-bold text-sm ${isBestPerformer ? "ring-2 ring-amber-400" : ""}`}>
                        {member.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    {isBestPerformer && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center">
                        <Crown className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Name & Role */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-800 dark:text-white truncate text-sm">{member.name}</p>
                      {isBestPerformer && (
                        <Badge className="bg-amber-500 text-white text-[9px] px-1.5 py-0">
                          <Sparkles className="w-2.5 h-2.5 mr-0.5" />
                          BEST
                        </Badge>
                      )}
                      {isCurrentUser && !isBestPerformer && (
                        <Badge variant="outline" className="text-[9px] border-indigo-400 text-indigo-600 px-1.5 py-0">YOU</Badge>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 capitalize">{member.role}</p>
                  </div>

                  {/* Score Breakdown - Compact */}
                  <div className="hidden sm:flex items-center gap-3 text-xs">
                    <div className="text-center">
                      <p className="text-[10px] text-slate-400">Tasks</p>
                      <p className="font-semibold text-violet-600">{member.tasks_score}%</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-slate-400">Attend.</p>
                      <p className="font-semibold text-amber-600">{member.attendance_score}%</p>
                    </div>
                  </div>

                  {/* Total Score */}
                  <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center ${
                    isBestPerformer ? "bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-lg" :
                    "bg-slate-100 dark:bg-slate-700"
                  }`}>
                    <span className={`text-lg font-bold ${isBestPerformer ? "text-white" : "text-slate-700 dark:text-white"}`}>
                      {member.weighted_score}
                    </span>
                    <span className={`text-[9px] ${isBestPerformer ? "text-white/80" : "text-slate-500"}`}>pts</span>
                  </div>
                </div>

                {/* Mobile Score Breakdown */}
                <div className="sm:hidden grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                  <div className="text-center">
                    <p className="text-[10px] text-slate-400">Tasks</p>
                    <p className="text-xs font-semibold text-violet-600">{member.tasks_score}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-slate-400">Attendance</p>
                    <p className="text-xs font-semibold text-amber-600">{member.attendance_score}%</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary Footer */}
        <div className="mt-4 pt-3 border-t border-amber-200 dark:border-amber-800">
          <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-400">
            <span>{leaderboard.length} team members ranked</span>
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3 text-amber-500" />
              Based on Tasks ({weights?.tasks}%) + Attendance ({weights?.attendance}%)
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BestPerformerCard;
